/**
 * Document repository — the app's single read path
 *
 * Screens read from here, never from the network. That is what makes offline
 * and online the same code path (FR-007): the only difference is how fresh the
 * documents are, not where the screen gets them.
 *
 * Documents are stored exactly as the API returned them (`data` verbatim), so
 * a backend that adds a field does not require a change here — see
 * specs/013-offline-first-pwa/data-model.md.
 */

import { openAccountDb, audioCacheName } from './db';
import { nowIso } from './clockSkew';

let currentSub = null;
let dbPromise = null;
let evictAudio = null;

// The handle is cached, so it has to be droppable: `openAccountDb` gives it
// up when another tab needs to upgrade the schema, and a failed open must not
// be cached forever. Either way the next read reopens.
const open = (sub) => {
  const opening = openAccountDb(sub, () => {
    if (currentSub === sub) dbPromise = null;
  });
  // Stored, not necessarily awaited — `setAccount` binds the account long
  // before anything reads. Without a handler here a failure is reported as an
  // unhandled rejection; callers that do await still see it.
  opening.catch(() => {});
  return opening;
};

/** Bind the repository to a signed-in account. Idempotent. */
export const setAccount = (sub) => {
  if (sub === currentSub) return;
  currentSub = sub || null;
  dbPromise = currentSub ? open(currentSub) : null;
};

export const clearAccount = () => {
  currentSub = null;
  dbPromise = null;
};

export const getAccount = () => currentSub;

/**
 * Register the audio-cache evictor used when content disappears server-side
 * (FR-017). Kept as a hook so this module does not depend on the download
 * layer, which arrives with User Story 1.
 */
export const registerAudioEvictor = (fn) => {
  evictAudio = typeof fn === 'function' ? fn : null;
};

const db = () => {
  if (!currentSub) throw new Error('repository: no account bound — call setAccount(sub) first');
  if (!dbPromise) dbPromise = open(currentSub);
  return dbPromise;
};

export const docId = (type, id) => `${type}:${id}`;

// ── meta ────────────────────────────────────────────────────────────────────

export const getMeta = async (key) => (await db()).get('meta', key);
export const setMeta = async (key, value) => (await db()).put('meta', value, key);

// ── reads ───────────────────────────────────────────────────────────────────

// ponytail: reads every doc and filters in memory. Correct and instant at the
// expected scale (O(10s) of projects, O(100s) of chapters). Add a `by_type`
// index if a library ever grows past a few thousand documents.
const allOfType = async (type) => {
  const rows = await (await db()).getAll('docs');
  return rows.filter((r) => r.type === type);
};

export const getProjects = async () => (await allOfType('project')).map((r) => r.data);

export const getProject = async (projectId) => {
  const row = await (await db()).get('docs', docId('project', projectId));
  return row ? row.data : null;
};

export const getChapters = async (projectId) => {
  const rows = await (await db()).getAllFromIndex('docs', 'by_project_type', [projectId, 'chapter']);
  return rows.map((r) => r.data);
};

export const getText = async (projectId, chapterId) => {
  const row = await (await db()).get('docs', docId('text', chapterId));
  return row && row.projectId === projectId ? row.data : null;
};

export const getDownload = async (chapterId) => (await db()).get('downloads', chapterId);
export const getDownloads = async () => (await db()).getAll('downloads');
export const putDownload = async (row) => (await db()).put('downloads', row);
export const deleteDownload = async (chapterId) => (await db()).delete('downloads', chapterId);

/**
 * Record that the device's copy was refreshed from the server.
 *
 * Distinct from `lastSyncedAt`, which belongs to the sync engine: while the
 * sync endpoint does not exist, the write-through cache is the only thing that
 * ever brings fresh data down, and it is what the user is actually looking at.
 */
const touchCache = () => setMeta('lastCachedAt', nowIso());

// ── write-through caching ───────────────────────────────────────────────────
//
// Until `GET /sync/changes` exists (contracts/sync-api.md), the screens are the
// only thing that ever sees fresh server data. Caching what they already fetch
// gives offline reads now, and costs nothing later: the sync engine writes into
// exactly the same store, so when the endpoint lands these calls become
// redundant rather than wrong.
//
// ponytail: replace-all per collection, which is how a deletion propagates
// without tombstones. It cannot see a project that vanished while this device
// was offline — that needs the sync endpoint (FR-017).

// A project's id has three spellings across the API surface: `id` from
// GET /projects and GET /shares/received, `project_id` from the sync feed, and
// `file_id` from the older per-file shapes. Accept all three — guessing wrong
// makes a project look unknown, and an unknown project gets pruned.
const idOf = (entity) => entity?.id || entity?.project_id || entity?.file_id || null;

const ABSENT_KEY = 'absentOnce';

/**
 * Two strikes before deleting downloaded audio (FR-038).
 *
 * Returns the ids absent for a second consecutive successful fetch, and
 * records a first strike against the rest. Ids seen again lose their strike.
 * Keys outside `evaluated` are left alone, so a project sweep does not clear
 * strikes recorded against chapters.
 */
async function strikeAbsent(absentIds, evaluatedIds) {
  const previous = (await getMeta(ABSENT_KEY)) || {};
  const carried = { ...previous };
  const doomed = [];

  for (const id of evaluatedIds) delete carried[id];
  for (const id of absentIds) {
    if (previous[id]) doomed.push(id);
    else carried[id] = true;
  }

  await setMeta(ABSENT_KEY, carried);
  return doomed;
}

/** Delete the cached audio and download rows for a set of chapters. */
async function evictDownloads(rows) {
  if (rows.length === 0) return;
  const handle = await db();
  const tx = handle.transaction('downloads', 'readwrite');
  for (const row of rows) tx.objectStore('downloads').delete(row.chapterId);
  await tx.done;

  if (!evictAudio) return;
  for (const row of rows) {
    try {
      await evictAudio(row.projectId, row.chapterId);
    } catch (e) {
      console.warn('repository: audio eviction failed', e);
    }
  }
}

/** Remove every document belonging to the given projects. */
async function deleteDocsForProjects(projectIds) {
  if (projectIds.size === 0) return;
  const handle = await db();
  const rows = await handle.getAll('docs');
  const tx = handle.transaction('docs', 'readwrite');
  for (const row of rows) {
    if (projectIds.has(row.projectId)) tx.objectStore('docs').delete(row.id);
  }
  await tx.done;
}

/**
 * Replace the cached set of the user's own projects.
 *
 * A project absent from the list has been deleted server-side (FR-017): its
 * documents go immediately, its downloaded audio on the second consecutive
 * absence (FR-038).
 *
 * Passing anything that is not an array is a no-op. That is the FR-037 guard:
 * a malformed or truncated response must never be read as "you have no
 * projects" and wipe the library.
 */
export const cacheProjects = async (projects) => {
  if (!Array.isArray(projects)) return;
  const handle = await db();
  const fresh = new Map(projects.map((p) => [idOf(p), p]).filter(([id]) => id));

  // Persist first, reconcile second. The caller fires this without awaiting it,
  // so a user who navigates away quickly can have the page torn down mid-write
  // — and the fresh data is what must survive that, not the cleanup.
  const writeTx = handle.transaction('docs', 'readwrite');
  for (const [id, data] of fresh) {
    writeTx.objectStore('docs').put({ id: docId('project', id), type: 'project', projectId: id, updatedAt: null, data });
  }
  await writeTx.done;

  const existing = await handle.getAll('docs');
  // Staleness is decided on the row's own `projectId`, which we set when we
  // wrote it, never on a field dug out of the server payload. The sync feed and
  // the REST endpoints spell the id differently, and a payload we failed to
  // read would look like a project that no longer exists — pruning it along
  // with its downloaded audio.
  const staleProjectIds = new Set(
    existing
      .filter((r) => r.type === 'project' && !fresh.has(r.projectId))
      .map((r) => r.projectId),
  );
  await deleteDocsForProjects(staleProjectIds);

  // Audio outlives the metadata by one round, so the download index — not the
  // documents — is what carries a project across the two strikes.
  const downloads = await handle.getAll('downloads');
  const absentProjects = new Set(downloads.map((r) => r.projectId).filter((id) => !fresh.has(id)));
  const doomed = await strikeAbsent(absentProjects, new Set(fresh.keys()));
  await evictDownloads(downloads.filter((r) => doomed.includes(r.projectId)));
  await touchCache();
};

/**
 * Replace the cached chapters of one project. Same rules as `cacheProjects`,
 * scoped to chapters: absent means deleted, audio on the second absence.
 */
export const cacheChapters = async (projectId, chapters) => {
  if (!projectId || !Array.isArray(chapters)) return;
  const handle = await db();
  const fresh = new Map(chapters.map((c) => [c?.chapter_id, c]).filter(([id]) => id));

  // Persist first, reconcile second — see cacheProjects.
  const writeTx = handle.transaction('docs', 'readwrite');
  for (const [id, data] of fresh) {
    writeTx.objectStore('docs').put({ id: docId('chapter', id), type: 'chapter', projectId, updatedAt: null, data });
  }
  await writeTx.done;

  const existing = await handle.getAllFromIndex('docs', 'by_project_type', [projectId, 'chapter']);
  const stale = existing.filter((r) => !fresh.has(r.data && r.data.chapter_id));
  if (stale.length > 0) {
    const pruneTx = handle.transaction('docs', 'readwrite');
    for (const row of stale) pruneTx.objectStore('docs').delete(row.id);
    await pruneTx.done;
  }

  const downloads = (await handle.getAll('downloads')).filter((r) => r.projectId === projectId);
  const absentChapters = new Set(downloads.map((r) => r.chapterId).filter((id) => !fresh.has(id)));
  const doomed = await strikeAbsent(absentChapters, new Set(fresh.keys()));
  await evictDownloads(downloads.filter((r) => doomed.includes(r.chapterId)));
  await touchCache();
};

/** Cache one chapter's readable text and key points (FR-002). */
export const cacheText = async (projectId, chapterId, data) => {
  if (!projectId || !chapterId || !data) return;
  await (await db()).put('docs', {
    id: docId('text', chapterId), type: 'text', projectId, updatedAt: null, data,
  });
  await touchCache();
};

/** Whether this device has ever cached anything for the signed-in account. */
export const hasCachedData = async () => {
  if (!dbPromise) return false;
  return (await (await db()).count('docs')) > 0;
};

// ── writes ──────────────────────────────────────────────────────────────────

/**
 * Delete a project and everything hanging off it: its chapters, its text, its
 * download rows and its cached audio (FR-017). A user who loses access to a
 * project must not be left able to play it.
 */
const deleteProjectCascade = async (projectId) => {
  const handle = await db();
  const rows = await handle.getAll('docs');
  const doomed = rows.filter((r) => r.projectId === projectId);
  const chapterIds = doomed.filter((r) => r.type === 'chapter').map((r) => r.data?.chapter_id).filter(Boolean);

  const tx = handle.transaction(['docs', 'downloads'], 'readwrite');
  for (const row of doomed) tx.objectStore('docs').delete(row.id);
  for (const chapterId of chapterIds) tx.objectStore('downloads').delete(chapterId);
  await tx.done;

  if (evictAudio) {
    for (const chapterId of chapterIds) {
      try {
        await evictAudio(projectId, chapterId);
      } catch (e) {
        console.warn('repository: audio eviction failed', e);
      }
    }
  }
};

/**
 * Apply one change from the sync stream. Idempotent: the server is allowed to
 * replay a change, and applying it twice must be indistinguishable from once.
 */
export const applyChange = async (change) => {
  if (!change || !change.type || !change.id) return;
  const { type, id, deleted } = change;

  if (deleted) {
    if (type === 'project') return deleteProjectCascade(id);
    return (await db()).delete('docs', docId(type, id));
  }

  return (await db()).put('docs', {
    id: docId(type, id),
    type,
    projectId: type === 'project' ? id : change.project_id,
    updatedAt: change.updated_at,
    data: change.data,
  });
};

/**
 * Apply a page of changes. Tombstones are applied last so a project deleted in
 * the same page as one of its chapter updates cannot resurrect the child.
 */
export const applyChanges = async (changes) => {
  if (!Array.isArray(changes)) return;
  for (const change of changes.filter((c) => !c?.deleted)) await applyChange(change);
  for (const change of changes.filter((c) => c?.deleted)) await applyChange(change);
};

/**
 * Drop every cached document, keeping downloads and cached audio. Used when the
 * server reports the sync cursor is too old to serve (410) and the device has to
 * rebuild its view from scratch — re-downloading gigabytes of audio for that
 * would be an unreasonable cost to pass to the user.
 */
export const clearDocuments = async () => {
  await (await db()).clear('docs');
};

/**
 * Remove every trace of a project from this device: its documents, its
 * chapters, its text, its download rows and its cached audio.
 *
 * Called when the user deletes a project or leaves a shared one. Reconciliation
 * would eventually notice it is gone, but only on the next successful fetch —
 * and a project the user just deleted should not still be sitting on their
 * device, occupying space, until then.
 */
export const forgetProject = async (projectId) => {
  if (!projectId) return;
  await applyChange({ type: 'project', id: projectId, deleted: true });
};

// Test-only: the raw database handle, so write ordering can be observed.
export const openHandleForTest = () => db();

export const audioCacheForAccount = () => (currentSub ? audioCacheName(currentSub) : null);
